/**
 * Utility functions for managing user lessons and preventing duplicates
 * Optimized with caching and reduced network requests
 */

import { generateClient } from "aws-amplify/api";
import { listUserLessons } from "../graphql/queries";
import { createUserLesson as createUserLessonMutation } from "../graphql/mutations";

// Cache configuration
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes
const DEFAULT_PAGE_LIMIT = 200; // Large page size to reduce pagination calls
const TARGETED_LIMIT = 50; // Limit for targeted queries

// Timeout / retry configuration
const API_TIMEOUT_MS = 30 * 1000; // 30 seconds per page request
const INFLIGHT_TIMEOUT_MS = 45 * 1000; // 45 seconds before giving up on an in-flight request
const MAX_RETRY_ATTEMPTS = 3;

export const withTimeout = (promise, ms, label = 'request') => {
  const timeout = new Promise((_, reject) =>
    setTimeout(() => reject(new Error(`Timeout: ${label} exceeded ${ms}ms`)), ms)
  );
  return Promise.race([promise, timeout]);
};

const retryWithBackoff = async (fn, maxAttempts = MAX_RETRY_ATTEMPTS, label = 'request') => {
  const delays = [1000, 3000, 9000];
  let lastError;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (attempt < maxAttempts) {
        const delay = delays[attempt - 1] ?? 9000;
        console.warn(`${label}: attempt ${attempt} failed, retrying in ${delay}ms...`, error.message);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw lastError;
};

// In-flight creation tracking to prevent race conditions
const inflightCreations = new Map(); // key: userID:lessonID -> Promise

/**
 * UserLessonCache - Per-user in-memory cache with memoized promises
 */
class UserLessonCache {
  constructor() {
    this.caches = new Map(); // userID -> CacheSnapshot
  }

  /**
   * Gets cache snapshot for a user
   * @param {string} userID - User ID
   * @returns {Object|null} - Cache snapshot or null if not cached/expired
   */
  getSnapshot(userID) {
    const cache = this.caches.get(userID);
    if (!cache) return null;
    
    const now = Date.now();
    if (now - cache.loadedAt > CACHE_TTL_MS) {
      // Expired, remove from cache
      this.caches.delete(userID);
      return null;
    }
    
    return cache;
  }

  /**
   * Sets cache snapshot for a user
   * @param {string} userID - User ID
   * @param {Array} items - Array of user lessons
   * @returns {Object} - Cache snapshot
   */
  setSnapshot(userID, items) {
    const snapshot = {
      items: items || [],
      byLessonID: new Map(),
      bestByLessonID: new Map(),
      loadedAt: Date.now(),
      inflight: null
    };

    // Group by lessonID and compute best records
    const groups = new Map();
    for (const item of snapshot.items) {
      if (!item.lessonID) continue;
      
      if (!groups.has(item.lessonID)) {
        groups.set(item.lessonID, []);
      }
      groups.get(item.lessonID).push(item);
    }

    // Compute best records using canonical comparator
    for (const [lessonID, lessons] of groups) {
      snapshot.byLessonID.set(lessonID, lessons);
      const best = lessons.length === 1 ? lessons[0] : lessons.sort(selectBestUserLesson)[0];
      snapshot.bestByLessonID.set(lessonID, best);
    }

    this.caches.set(userID, snapshot);
    return snapshot;
  }

  /**
   * Gets best user lesson for a specific lesson from cache
   * @param {string} userID - User ID
   * @param {string} lessonID - Lesson ID
   * @returns {Object|null} - Best user lesson or null if not cached
   */
  getBest(userID, lessonID) {
    const snapshot = this.getSnapshot(userID);
    return snapshot ? snapshot.bestByLessonID.get(lessonID) || null : null;
  }

  /**
   * Checks if cache has any record for a lesson
   * @param {string} userID - User ID
   * @param {string} lessonID - Lesson ID
   * @returns {boolean} - True if lesson exists in cache
   */
  hasLesson(userID, lessonID) {
    const snapshot = this.getSnapshot(userID);
    return snapshot ? snapshot.bestByLessonID.has(lessonID) : false;
  }

  /**
   * Adds or updates a single user lesson in cache
   * @param {string} userID - User ID
   * @param {Object} userLesson - User lesson to add/update
   */
  updateLesson(userID, userLesson) {
    const snapshot = this.getSnapshot(userID);
    if (!snapshot || !userLesson.lessonID) return;

    // Add to items array
    const existingIndex = snapshot.items.findIndex(ul => ul.id === userLesson.id);
    if (existingIndex >= 0) {
      snapshot.items[existingIndex] = userLesson;
    } else {
      snapshot.items.push(userLesson);
    }

    // Update lesson maps
    const lessonID = userLesson.lessonID;
    const existing = snapshot.byLessonID.get(lessonID) || [];
    const updatedGroup = existing.filter(ul => ul.id !== userLesson.id);
    updatedGroup.push(userLesson);
    
    snapshot.byLessonID.set(lessonID, updatedGroup);
    
    // Recompute best for this lesson
    const best = updatedGroup.length === 1 ? updatedGroup[0] : updatedGroup.sort(selectBestUserLesson)[0];
    snapshot.bestByLessonID.set(lessonID, best);
  }

  /**
   * Invalidates cache for a user (call after bulk operations)
   * @param {string} userID - User ID to invalidate
   */
  invalidate(userID) {
    this.caches.delete(userID);
  }

  /**
   * Invalidates all caches
   */
  invalidateAll() {
    this.caches.clear();
  }

  /**
   * Gets in-flight promise for user if any
   * @param {string} userID - User ID
   * @returns {Promise|null} - In-flight promise or null
   */
  getInflight(userID) {
    const snapshot = this.caches.get(userID);
    return snapshot ? snapshot.inflight : null;
  }

  /**
   * Sets in-flight promise for user
   * @param {string} userID - User ID
   * @param {Promise} promise - Promise to store
   */
  setInflight(userID, promise) {
    let snapshot = this.caches.get(userID);
    if (!snapshot) {
      // Create minimal snapshot to store inflight
      snapshot = {
        items: [],
        byLessonID: new Map(),
        bestByLessonID: new Map(),
        loadedAt: 0, // Mark as not loaded
        inflight: null
      };
      this.caches.set(userID, snapshot);
    }
    snapshot.inflight = promise;
  }

  /**
   * Clears in-flight promise for user
   * @param {string} userID - User ID
   */
  clearInflight(userID) {
    const snapshot = this.caches.get(userID);
    if (snapshot) {
      snapshot.inflight = null;
    }
  }
}

// Global cache instance
const userLessonCache = new UserLessonCache();

/**
 * Selects the best user lesson record from two candidates based on priority rules
 * Priority: 1) Completed (progress=100), 2) Highest progress, 3) Most recent timestamp
 * @param {Object} a - First user lesson
 * @param {Object} b - Second user lesson
 * @returns {number} - Negative if a is better, positive if b is better, 0 if equal
 */
export function selectBestUserLesson(a, b) {
  // 1) completed first
  if (a.progress === 100 && b.progress !== 100) return -1;
  if (a.progress !== 100 && b.progress === 100) return 1;
  // 2) higher progress
  if (a.progress !== b.progress) return b.progress - a.progress;
  // 3) newest timestamp
  const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
  const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
  return tb - ta;
}

/**
 * Deduplicates user lessons by keeping only the best record for each user-lesson combination
 * Uses selectBestUserLesson to determine which record to keep
 * @param {Array} items - Array of user lesson objects
 * @returns {Array} - Deduplicated array of user lessons
 */
export function deduplicateUserLessons(items) {
  const map = new Map(); // key: `${userID}:${lessonID}` -> best record
  for (const ul of items || []) {
    const key = `${ul.userID}:${ul.lessonID}`;
    const existing = map.get(key);
    if (!existing) {
      map.set(key, ul);
    } else {
      map.set(key, [existing, ul].sort(selectBestUserLesson)[0]);
    }
  }
  return Array.from(map.values());
}

/**
 * Finds duplicate user lessons grouped by user-lesson combination
 * @param {Array} userLessons - Array of user lesson objects
 * @returns {Object} - Object with duplicate groups keyed by userID-lessonID
 */
export const findDuplicateUserLessons = (userLessons) => {
  if (!Array.isArray(userLessons) || userLessons.length === 0) {
    return {};
  }

  const groups = new Map();

  userLessons.forEach(userLesson => {
    const key = `${userLesson.userID}-${userLesson.lessonID}`;
    if (!groups.has(key)) {
      groups.set(key, []);
    }
    groups.get(key).push(userLesson);
  });

  // Filter to only return groups with duplicates
  const duplicates = {};
  groups.forEach((group, key) => {
    if (group.length > 1) {
      // Sort by updatedAt/createdAt descending (newest first)
      group.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt || 0);
        const dateB = new Date(b.updatedAt || b.createdAt || 0);
        return dateB - dateA;
      });
      duplicates[key] = group;
    }
  });

  return duplicates;
};

/**
 * Checks if a user lesson already exists for a specific user-lesson combination
 * @param {Array} userLessons - Array of existing user lessons
 * @param {string} userID - User ID to check
 * @param {string} lessonID - Lesson ID to check
 * @returns {Object|null} - Existing user lesson or null if not found
 */
export const findExistingUserLesson = (userLessons, userID, lessonID) => {
  if (!Array.isArray(userLessons) || !userID || !lessonID) {
    return null;
  }

  return userLessons.find(ul => ul.userID === userID && ul.lessonID === lessonID) || null;
};

/**
 * Validates user lesson data before creation
 * @param {Object} userLessonData - User lesson data to validate
 * @returns {Object} - Validation result with isValid boolean and errors array
 */
export const validateUserLessonData = (userLessonData) => {
  const errors = [];

  if (!userLessonData.userID) {
    errors.push('User ID is required');
  }

  if (!userLessonData.lessonID) {
    errors.push('Lesson ID is required');
  }

  if (userLessonData.progress !== undefined && (userLessonData.progress < 0 || userLessonData.progress > 100)) {
    errors.push('Progress must be between 0 and 100');
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Gets the best user lesson for a specific user-lesson combination from the provided array
 * @param {Array} userLessons - Array of user lessons to search
 * @param {string} userID - User ID to find
 * @param {string} lessonID - Lesson ID to find
 * @returns {Object|null} - Best user lesson or null if not found
 */
export const getBestUserLesson = (userLessons, userID, lessonID) => {
  if (!Array.isArray(userLessons) || !userID || !lessonID) {
    return null;
  }

  const matches = userLessons.filter(ul => ul.userID === userID && ul.lessonID === lessonID);
  if (matches.length === 0) return null;
  if (matches.length === 1) return matches[0];
  
  // Multiple matches, select the best one
  return matches.sort(selectBestUserLesson)[0];
};

/**
 * Creates a deduped map of best user lessons organized by user and lesson
 * @param {Array} userLessons - Array of user lessons
 * @returns {Object} - Nested object: { [userID]: { [lessonID]: bestUserLesson } }
 */
export const createBestUserLessonMap = (userLessons) => {
  const map = {};
  
  if (!Array.isArray(userLessons)) return map;
  
  userLessons.forEach(ul => {
    if (!ul.userID || !ul.lessonID) return;
    
    if (!map[ul.userID]) {
      map[ul.userID] = {};
    }
    
    const existing = map[ul.userID][ul.lessonID];
    if (!existing) {
      map[ul.userID][ul.lessonID] = ul;
    } else {
      // Keep the better one using canonical comparator
      map[ul.userID][ul.lessonID] = [existing, ul].sort(selectBestUserLesson)[0];
    }
  });
  
  return map;
};

/**
 * Ensures user lessons are loaded with caching and memoized promises
 * This is the primary entry point for getting all user lessons
 * @param {string} userID - User ID to fetch lessons for
 * @param {boolean} forceRefresh - Force refresh even if cached (default: false)
 * @returns {Promise<Object>} - Cache snapshot with items, byLessonID, bestByLessonID
 */
export const ensureUserLessonsLoaded = async (userID, forceRefresh = false) => {
  if (!userID) {
    throw new Error('userID is required for ensureUserLessonsLoaded');
  }

  // Check for valid cache first
  if (!forceRefresh) {
    const cached = userLessonCache.getSnapshot(userID);
    if (cached) {
      console.log(`ensureUserLessonsLoaded: cache hit for user ${userID} (${cached.items.length} items)`);
      return cached;
    }
  }

  // Check for in-flight request (with timeout so it never blocks forever)
  const inflight = userLessonCache.getInflight(userID);
  if (inflight) {
    console.log(`ensureUserLessonsLoaded: waiting for in-flight request for user ${userID}`);
    return await withTimeout(inflight, INFLIGHT_TIMEOUT_MS, 'inflight request');
  }

  // Start new fetch with retry + timeout
  const fetchPromise = (async () => {
    try {
      console.log(`ensureUserLessonsLoaded: starting fetch for user ${userID}`);
      const items = await retryWithBackoff(
        () => fetchAllUserLessonsFromAPI(userID),
        MAX_RETRY_ATTEMPTS,
        `fetchAllUserLessonsFromAPI for user ${userID}`
      );
      const snapshot = userLessonCache.setSnapshot(userID, items);
      console.log(`ensureUserLessonsLoaded: fetched ${items.length} items for user ${userID}`);
      return snapshot;
    } catch (error) {
      console.error(`Error in ensureUserLessonsLoaded for user ${userID}:`, error);
      throw error;
    } finally {
      userLessonCache.clearInflight(userID);
    }
  })();

  userLessonCache.setInflight(userID, fetchPromise);
  return await fetchPromise;
};

/**
 * Internal function to fetch all user lessons from API with optimized pagination
 * @param {string} userID - User ID to fetch lessons for
 * @param {number} pageLimit - Page size per request (default: 200 for fewer calls)
 * @returns {Promise<Array>} - Complete array of all user lessons for the user
 */
const fetchAllUserLessonsFromAPI = async (userID, pageLimit = DEFAULT_PAGE_LIMIT) => {
  const client = generateClient();
  let allItems = [];
  let nextToken = null;
  let pageCount = 0;

  do {
    pageCount++;
    
    const result = await withTimeout(
      client.graphql({
        query: listUserLessons,
        variables: {
          filter: { userID: { eq: userID } },
          limit: pageLimit,
          nextToken: nextToken
        },
      }),
      API_TIMEOUT_MS,
      `fetchAllUserLessonsFromAPI page ${pageCount}`
    );

    const pageItems = result.data.listUserLessons.items || [];
    allItems = allItems.concat(pageItems);
    nextToken = result.data.listUserLessons.nextToken;
  } while (nextToken);

  console.log(`fetchAllUserLessonsFromAPI: fetched ${allItems.length} items in ${pageCount} page(s) for user ${userID}`);
  return allItems;
};

/**
 * Legacy function - now delegates to ensureUserLessonsLoaded for backward compatibility
 * @deprecated Use ensureUserLessonsLoaded instead
 */
export const listAllUserLessonsForUser = async (userID, pageLimit = DEFAULT_PAGE_LIMIT) => {
  const snapshot = await ensureUserLessonsLoaded(userID);
  return snapshot.items;
};

/**
 * Cache-aware targeted lookup for a specific user-lesson combination
 * Checks cache first, then performs targeted API query if needed
 * @param {string} userID - User ID
 * @param {string} lessonID - Lesson ID
 * @returns {Promise<Object|null>} - Best matching user lesson or null if not found
 */
export const getUserLessonByUserAndLesson = async (userID, lessonID) => {
  if (!userID || !lessonID) {
    throw new Error('Both userID and lessonID are required for getUserLessonByUserAndLesson');
  }

  // 1. Check cache first
  const cached = userLessonCache.getBest(userID, lessonID);
  if (cached) {
    console.log(`getUserLessonByUserAndLesson: cache hit for ${userID}:${lessonID} (progress ${cached.progress}%)`);
    return cached;
  }

  // 2. Check if we have a full cache - if so, lesson doesn't exist
  const snapshot = userLessonCache.getSnapshot(userID);
  if (snapshot && snapshot.loadedAt > 0) {
    console.log(`getUserLessonByUserAndLesson: cache miss for ${userID}:${lessonID} (not found in full cache)`);
    return null;
  }

  // 3. Perform targeted API query
  console.log(`getUserLessonByUserAndLesson: performing targeted lookup for ${userID}:${lessonID}`);

  const client = generateClient();
  const result = await withTimeout(
    client.graphql({
      query: listUserLessons,
      variables: {
        filter: {
          and: [
            { userID: { eq: userID } },
            { lessonID: { eq: lessonID } }
          ]
        },
        limit: TARGETED_LIMIT
      },
    }),
    API_TIMEOUT_MS,
    `getUserLessonByUserAndLesson ${userID}:${lessonID}`
  );

  const matches = result.data.listUserLessons.items || [];
  
  if (matches.length === 0) {
    console.log(`getUserLessonByUserAndLesson: no records found for ${userID}:${lessonID}`);
    return null;
  }

  // Select best match and update cache
  const bestMatch = matches.length === 1 ? matches[0] : matches.sort(selectBestUserLesson)[0];
  
  // Update cache with this finding
  matches.forEach(match => userLessonCache.updateLesson(userID, match));
  
  console.log(`getUserLessonByUserAndLesson: found ${matches.length} record(s) for ${userID}:${lessonID}, selected best with progress ${bestMatch.progress}%`);
  
  return bestMatch;
};

/**
 * Cache-aware race-safe idempotent function to find or create a user lesson
 * This is the ONLY function that should be used to create user lessons
 * Uses cache hierarchy: cache → targeted lookup → creation (no parallel fetches)
 * @param {string} userID - User ID
 * @param {string} lessonID - Lesson ID  
 * @param {Function} createPayloadFactory - Function that returns the payload for creation
 * @returns {Promise<Object>} - The existing or newly created user lesson
 */
export const findOrCreateUserLesson = async (userID, lessonID, createPayloadFactory) => {
  if (!userID || !lessonID) {
    throw new Error('userID and lessonID are required');
  }

  const key = `${userID}:${lessonID}`;
  
  console.log(`findOrCreateUserLesson called for ${key}`);
  
  // 1. Check cache first (fastest path)
  const cached = userLessonCache.getBest(userID, lessonID);
  if (cached) {
    console.log(`findOrCreateUserLesson: cache hit for ${key} (progress ${cached.progress}%)`);
    return cached;
  }
  
  // 2. Check if there's already an in-flight creation for this combination
  if (inflightCreations.has(key)) {
    console.log(`findOrCreateUserLesson: waiting for in-flight creation for ${key}`);
    return await inflightCreations.get(key);
  }
  
  // 3. Create promise for this creation and store it to prevent races
  const creationPromise = (async () => {
    try {
      // Use cache-aware targeted lookup
      console.log(`findOrCreateUserLesson: performing targeted lookup for ${key}`);
      const existingRecord = await getUserLessonByUserAndLesson(userID, lessonID);
      
      if (existingRecord) {
        // Found existing record, return it
        console.log(`findOrCreateUserLesson: existing record found for ${key} (progress ${existingRecord.progress}%)`);
        return existingRecord;
      }
      
      // 4. No existing record found, attempt creation with deterministic ID
      console.log(`findOrCreateUserLesson: creating new record for ${key}`);
      const payload = createPayloadFactory();
      
      // Use deterministic ID if not provided to help with idempotency
      if (!payload.id) {
        payload.id = `${userID}#${lessonID}`;
      }
      
      const client = generateClient();

      const createResult = await withTimeout(
        client.graphql({
          query: createUserLessonMutation,
          variables: {
            input: payload,
          },
        }),
        API_TIMEOUT_MS,
        `createUserLesson ${key}`
      );
      
      const newRecord = createResult.data.createUserLesson;
      
      // Update cache with new record
      userLessonCache.updateLesson(userID, newRecord);
      
      console.log(`findOrCreateUserLesson: created new record for ${key} with id ${newRecord.id}`);
      return newRecord;
      
    } catch (error) {
      // If creation failed due to duplicate ID or similar, try targeted lookup once more
      if (error.message && (error.message.includes('already exists') || error.message.includes('duplicate'))) {
        console.log(`findOrCreateUserLesson: duplicate detected, performing recovery lookup for ${key}`);
        try {
          const recoveredRecord = await getUserLessonByUserAndLesson(userID, lessonID);
          if (recoveredRecord) {
            console.log(`findOrCreateUserLesson: recovered existing record for ${key} (progress ${recoveredRecord.progress}%)`);
            return recoveredRecord;
          }
        } catch (fetchError) {
          console.error(`Error during recovery lookup for ${key}:`, fetchError);
        }
      }
      
      console.error(`Error in findOrCreateUserLesson for ${key}:`, error);
      throw error;
    }
  })();
  
  // Store the promise to prevent concurrent creations
  inflightCreations.set(key, creationPromise);
  
  try {
    const result = await creationPromise;
    return result;
  } finally {
    // Clean up the in-flight tracking
    inflightCreations.delete(key);
  }
};

/**
 * Invalidates cache for a user - call after bulk operations
 * @param {string} userID - User ID to invalidate
 */
export const invalidateUserLessonCache = (userID) => {
  userLessonCache.invalidate(userID);
};

/**
 * Invalidates all user lesson caches
 */
export const invalidateAllUserLessonCaches = () => {
  userLessonCache.invalidateAll();
};
